import React from "react";
import { useLocation } from "react-router-dom";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";

const CONTACT_EMAIL = "vishwaramm@gmail.com";

const PRIVACY_CONFIG = {
  "meditation-counter": {
    appName: "Mantra Counter",
    companyName: "Mantra Counter",
    country: "New York, United States",
    lastUpdated: "March 24, 2021",
  },
  "body-analyzer": {
    appName: "Body Analyzer",
    companyName: "Body Analyzer",
    country: "New York, United States",
    lastUpdated: "March 13, 2026",
  },
  "neon-jumper-199x": {
    appName: "Neon Jumper 199X",
    companyName: "Vishwaram Maharaj Apps",
    country: "New York, United States",
    lastUpdated: "March 13, 2026",
  },
  "crystal-relics": {
    appName: "Crystal Relics",
    companyName: "Vishwaram Maharaj Apps",
    country: "New York, United States",
    lastUpdated: "March 13, 2026",
  },
  default: {
    appName: "This Application",
    companyName: "Vishwaram Maharaj Apps",
    country: "New York, United States",
    lastUpdated: "March 13, 2026",
  },
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function PrivacySection({ title, children }) {
  return (
    <section className="section">
      <div className="home-v3-panel">
        <span className="home-v3-kicker">Privacy</span>
        <h2 className="section-title">{title}</h2>
        <div className="privacy-copy">{children}</div>
      </div>
    </section>
  );
}

export default function AppPrivacyPage() {
  const query = useQuery();
  const appKey = query.get("app") || "default";
  const config = PRIVACY_CONFIG[appKey] || PRIVACY_CONFIG.default;

  return (
    <SiteLayout>
      <main className="page-shell home-v3-shell">
        <PageIntro
          eyebrow="Privacy Policy"
          title={`Privacy for ${config.appName}.`}
          description={`Last updated ${config.lastUpdated}. Each app's policy is kept here so it is easy to find from the app, support page, or main site.`}
        />

        <PrivacySection title="Privacy Policy">
          <p>
            This Privacy Policy describes Our policies and procedures on the
            collection, use and disclosure of Your information when You use the
            Service and tells You about Your privacy rights and how the law
            protects You.
          </p>
          <p>
            We use Your Personal data to provide and improve the Service. By
            using the Service, You agree to the collection and use of
            information in accordance with this Privacy Policy.
          </p>
        </PrivacySection>

        <PrivacySection title="Interpretation and Definitions">
          <p>
            The words of which the initial letter is capitalized have meanings
            defined under the following conditions. The following definitions
            shall have the same meaning regardless of whether they appear in
            singular or in plural.
          </p>
          <div className="privacy-definition-list">
            <p>
              <strong>Account</strong> means a unique account created for You to
              access our Service or parts of our Service.
            </p>
            <p>
              <strong>Affiliate</strong> means an entity that controls, is
              controlled by or is under common control with a party, where
              &quot;control&quot; means ownership of 50% or more of the shares,
              equity interest or other securities entitled to vote for election
              of directors or other managing authority.
            </p>
            <p>
              <strong>Application</strong> means the software program provided
              by the Company downloaded by You on any electronic device, named{" "}
              {config.appName}.
            </p>
            <p>
              <strong>Company</strong> (referred to as either &quot;the
              Company&quot;, &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot;
              in this Agreement) refers to {config.companyName}.
            </p>
            <p>
              <strong>Country</strong> refers to: {config.country}
            </p>
            <p>
              <strong>Device</strong> means any device that can access the
              Service such as a computer, a cellphone or a digital tablet.
            </p>
            <p>
              <strong>Personal Data</strong> is any information that relates to
              an identified or identifiable individual.
            </p>
            <p>
              <strong>Service</strong> refers to the Application.
            </p>
            <p>
              <strong>Service Provider</strong> means any natural or legal
              person who processes the data on behalf of the Company. It refers
              to third-party companies or individuals employed by the Company to
              facilitate the Service, to provide the Service on behalf of the
              Company, to perform services related to the Service or to assist
              the Company in analyzing how the Service is used.
            </p>
            <p>
              <strong>Third-party Social Media Service</strong> refers to any
              website or any social network website through which a User can log
              in or create an account to use the Service.
            </p>
            <p>
              <strong>Usage Data</strong> refers to data collected
              automatically, either generated by the use of the Service or from
              the Service infrastructure itself.
            </p>
            <p>
              <strong>You</strong> means the individual accessing or using the
              Service, or the company, or other legal entity on behalf of which
              such individual is accessing or using the Service, as applicable.
            </p>
          </div>
        </PrivacySection>

        <PrivacySection title="Collecting and Using Your Personal Data">
          <h3 className="feature-title">Types of Data Collected</h3>
          <p>
            <strong>Personal Data</strong>
          </p>
          <p>
            While using Our Service, We may ask You to provide Us with certain
            personally identifiable information that can be used to contact or
            identify You.
          </p>
          <p>
            <strong>Usage Data</strong>
          </p>
          <p>Usage Data is collected automatically when using the Service.</p>
          <p>
            Usage Data may include information such as Your Device&apos;s
            Internet Protocol address, browser type, browser version, the pages
            of our Service that You visit, the time and date of Your visit, the
            time spent on those pages, unique device identifiers and other
            diagnostic data.
          </p>
          <p>
            When You access the Service by or through a mobile device, We may
            collect certain information automatically, including, but not
            limited to, the type of mobile device You use, Your mobile device
            unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use,
            unique device identifiers and other diagnostic data.
          </p>
          <p>
            We may also collect information that Your browser sends whenever You
            visit our Service or when You access the Service by or through a
            mobile device.
          </p>
        </PrivacySection>

        <PrivacySection title="Use of Your Personal Data">
          <div className="privacy-definition-list">
            <p>
              To provide and maintain our Service, including to monitor usage.
            </p>
            <p>
              To manage Your Account and registration as a user of the Service.
            </p>
            <p>
              For the performance of a contract relating to products, items or
              services You have purchased or any other contract with Us.
            </p>
            <p>
              To contact You by email, phone, SMS, or other equivalent forms of
              electronic communication, including updates or security notices.
            </p>
            <p>
              To provide You with news, special offers and general information
              about goods, services and events unless You opt out.
            </p>
            <p>To manage and respond to Your requests.</p>
            <p>
              For business transfers such as merger, divestiture, restructuring,
              reorganization, dissolution, or sale of assets.
            </p>
            <p>
              For other purposes such as data analysis, usage trends, campaign
              effectiveness, and to improve the Service and user experience.
            </p>
          </div>
        </PrivacySection>

        <PrivacySection title="Sharing of Your Personal Information">
          <div className="privacy-definition-list">
            <p>
              With Service Providers to monitor and analyze the use of our
              Service and to contact You.
            </p>
            <p>
              For business transfers, including merger, sale of Company assets,
              financing, or acquisition.
            </p>
            <p>
              With Affiliates, subject to their honoring this Privacy Policy.
            </p>
            <p>
              With business partners to offer You certain products, services or
              promotions.
            </p>
            <p>
              With other users if You share information publicly or use
              Third-Party Social Media Services.
            </p>
            <p>With Your consent for any other purpose.</p>
          </div>
        </PrivacySection>

        <PrivacySection title="Retention of Your Personal Data">
          <p>
            The Company will retain Your Personal Data only for as long as is
            necessary for the purposes set out in this Privacy Policy. We will
            retain and use Your Personal Data to the extent necessary to comply
            with legal obligations, resolve disputes, and enforce legal
            agreements and policies.
          </p>
          <p>
            Usage Data is generally retained for a shorter period of time,
            except when it is used to strengthen security, improve
            functionality, or when We are legally obligated to retain it longer.
          </p>
        </PrivacySection>

        <PrivacySection title="Transfer of Your Personal Data">
          <p>
            Your information, including Personal Data, may be processed at the
            Company&apos;s operating offices and in any other places where the
            parties involved in the processing are located. This may include
            transfers to computers located outside of Your jurisdiction.
          </p>
          <p>
            Your consent to this Privacy Policy followed by Your submission of
            such information represents Your agreement to that transfer.
          </p>
          <p>
            The Company will take all steps reasonably necessary to ensure that
            Your data is treated securely and in accordance with this Privacy
            Policy.
          </p>
        </PrivacySection>

        <PrivacySection title="Disclosure of Your Personal Data">
          <h3 className="feature-title">Business Transactions</h3>
          <p>
            If the Company is involved in a merger, acquisition or asset sale,
            Your Personal Data may be transferred.
          </p>
          <h3 className="feature-title">Law Enforcement</h3>
          <p>
            Under certain circumstances, the Company may be required to disclose
            Your Personal Data if required to do so by law or in response to
            valid requests by public authorities.
          </p>
          <h3 className="feature-title">Other Legal Requirements</h3>
          <div className="privacy-definition-list">
            <p>Comply with a legal obligation.</p>
            <p>Protect and defend the rights or property of the Company.</p>
            <p>
              Prevent or investigate possible wrongdoing in connection with the
              Service.
            </p>
            <p>Protect the personal safety of users or the public.</p>
            <p>Protect against legal liability.</p>
          </div>
        </PrivacySection>

        <PrivacySection title="Security of Your Personal Data">
          <p>
            The security of Your Personal Data is important to Us, but remember
            that no method of transmission over the Internet, or method of
            electronic storage is 100% secure. While We strive to use
            commercially acceptable means to protect Your Personal Data, We
            cannot guarantee its absolute security.
          </p>
        </PrivacySection>

        <PrivacySection title="Children's Privacy">
          <p>
            Our Service does not address anyone under the age of 13. We do not
            knowingly collect personally identifiable information from anyone
            under the age of 13.
          </p>
          <p>
            If You are a parent or guardian and You are aware that Your child
            has provided Us with Personal Data, please contact Us. If We become
            aware that We have collected Personal Data from anyone under the age
            of 13 without verification of parental consent, We take steps to
            remove that information from Our servers.
          </p>
        </PrivacySection>

        <PrivacySection title="Links to Other Websites">
          <p>
            Our Service may contain links to other websites that are not
            operated by Us. If You click on a third party link, You will be
            directed to that third party&apos;s site. We strongly advise You to
            review the Privacy Policy of every site You visit.
          </p>
          <p>
            We have no control over and assume no responsibility for the
            content, privacy policies or practices of any third party sites or
            services.
          </p>
        </PrivacySection>

        <PrivacySection title="Changes to this Privacy Policy">
          <p>
            We may update Our Privacy Policy from time to time. We will notify
            You of any changes by posting the new Privacy Policy on this page.
          </p>
          <p>
            We may also let You know via email or a prominent notice in the
            Service, prior to the change becoming effective, and update the
            &quot;Last updated&quot; date at the top of this Privacy Policy.
          </p>
          <p>
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </p>
        </PrivacySection>

        <PrivacySection title="Contact Us">
          <p>
            If you have any questions about this Privacy Policy, You can contact
            us by email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </PrivacySection>
      </main>
    </SiteLayout>
  );
}
